<template>
  <div>
    <div class="flex items-center justify-between gap-[1rem] mb-[.75rem]">
      <p class="text-sm text-[#828282] mb-0">
        Pengecualian hirarki divisi untuk staf tertentu.
      </p>
      <BButton
        variant="outline-primary"
        class="!flex items-center justify-center gap-[.25rem]"
        @click="handleTambahOverride"
      >
        <span class="k-add" />
        <span>Tambah Override</span>
      </BButton>
    </div>

    <BTable
      :items="overrideItems"
      :fields="overrideFields"
      :busy="isLoading"
      responsive
      show-empty
      empty-text="Tidak ada data yang ditampilkan."
      class="align-middle mb-0 bordered-body-b-table"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <BSpinner class="align-middle mr-1" />
          <span>Memuat data...</span>
        </div>
      </template>

      <template #cell(name)="data">
        <div>
          <div class="text-sm font-medium text-black">
            {{ data.item.name }}
          </div>
          <div
            v-if="data.item.role"
            class="text-xs text-[#828282]"
          >
            {{ data.item.role }}
          </div>
        </div>
      </template>

      <template #cell(approver)="data">
        <div>
          <div class="text-sm font-medium text-black">
            {{ data.item.approver }}
          </div>
          <div
            v-if="data.item.approverRole"
            class="text-xs text-[#828282]"
          >
            {{ data.item.approverRole }}
          </div>
        </div>
      </template>

      <template #cell(action)="data">
        <div class="flex items-center justify-end gap-[.5rem]">
          <BButton
            variant="link"
            size="sm"
            class="border !flex items-center gap-[.25rem] ml-auto"
            @click="handleManageOverride(data.item)"
          >
            <span class="k-edit-2 text-[16px] text-black" />
            <span class="text-xs font-medium text-black">Atur</span>
          </BButton>
          <BButton
            variant="outline-danger"
            size="sm"
            class="border !flex items-center gap-[.25rem]"
            @click="handleDeleteOverride(data.item)"
          >
            <span class="k-trash text-[16px] text-[#DC2626]" />
            <span class="text-xs font-medium text-[#DC2626]">Hapus</span>
          </BButton>
        </div>
      </template>
    </BTable>
    <ModalConfirmation :modal="modalConfirm" />
    <DrawerOverrideIndividu
      :visible="isDrawerVisible"
      :mode="drawerMode"
      :override-data="selectedOverride"
      :is-submitting="isSubmitting"
      @on-close="isDrawerVisible = false"
      @on-save="handleSaveOverride"
      @on-delete="handleDeleteOverride"
    />
  </div>
</template>

<style scoped>
::v-deep .btn-outline-primary,
::v-deep .btn-outline-danger,
::v-deep .btn-link {
  border-radius: 10px !important;
}
</style>

<script>
import { komtimAxiosIns } from '@/libs/axios'
import ModalConfirmation from '../../components/ModalConfirmation.vue'
import DrawerOverrideIndividu from './DrawerOverrideIndividu.vue'
import { confirmModalMessage } from '../../helpers/string-html'
import {
  tableOverrideFields,
  showSuccess,
  showError,
  createUserOverrideConfig,
  updateUserOverrideConfig,
  deleteUserOverrideConfig,
} from '../config'

export default {
  name: 'TabOverrideIndividu',
  components: {
    ModalConfirmation,
    DrawerOverrideIndividu,
  },
  data() {
    return {
      overrideFields: tableOverrideFields,
      isDrawerVisible: false,
      drawerMode: 'add', // 'add' | 'edit'
      selectedOverride: {},
      overrideItems: [],
      isLoading: false,
      isSubmitting: false,
      modalConfirm: {
        id: 'modal-confirm-delete-override',
        title: '',
        description: '',
        negativeButtonAction: () => {},
        negativeButtonText: '',
        positiveButtonAction: () => {},
        positiveButtonText: '',
        closeButtonAction: () => {},
      },
    }
  },
  mounted() {
    this.fetchUserOverrideConfigs()
  },
  methods: {
    async fetchUserOverrideConfigs() {
      this.isLoading = true
      try {
        const response = await komtimAxiosIns.get('v1/overtime_configurations/users')
        const raw = response?.data?.data
        let data = []
        if (Array.isArray(raw)) {
          data = raw
        } else if (Array.isArray(raw?.items)) {
          data = raw.items
        }
        this.overrideItems = data.map(item => {
          let approverName = item.approver_1_name || item.approver_name || '-'
          let approverRole = item.approver_1_role || item.approver_role || ''
          let approverId = item.approver_1_id || item.approver_id

          if (Array.isArray(item.approvers) && item.approvers.length > 0) {
            const step1 = item.approvers.find(a => a.step === 1) || item.approvers[0]
            if (step1) {
              approverId = step1.user_id || step1.id || approverId
              approverName = step1.user_name || step1.name || approverName
              approverRole = step1.user_role || step1.role || step1.position_name || approverRole
            }
          }

          return {
            id: item.id,
            user_id: item.user_id,
            name: item.user_name || item.name || '-',
            role: item.user_role || item.role || item.position_name || '',
            approver_1_id: approverId,
            approver: approverName,
            approverRole,
            approver_final_id: item.approver_final_id,
            approverFinal: item.approver_final_name || '-',
            total_approvals: item.total_approvals,
            is_override: item.is_override,
            created_at: item.created_at,
            updated_at: item.updated_at,
            raw: item,
          }
        })
      } catch (error) {
        showError(error, 'Gagal memuat data override individu')
      } finally {
        this.isLoading = false
      }
    },
    handleTambahOverride() {
      this.drawerMode = 'add'
      this.selectedOverride = {}
      this.isDrawerVisible = true
    },
    handleManageOverride(item) {
      this.drawerMode = 'edit'
      this.selectedOverride = item
      this.isDrawerVisible = true
    },
    async handleSaveOverride({
      mode,
      id,
      employee,
      approver,
      overrideData,
    }) {
      this.isSubmitting = true
      try {
        const approverUserId = Number(
          approver?.id || approver?.user_id || approver?.approver_1_id || overrideData?.approver_1_id,
        )
        const employeeUserId = Number(
          employee?.id || employee?.user_id || overrideData?.user_id,
        )

        const payload = {
          user_id: employeeUserId,
          is_override: 1,
          approvers: [
            {
              user_id: approverUserId,
              step: 1,
              is_final: true,
            },
          ],
        }

        if (mode === 'edit' && id) {
          await updateUserOverrideConfig(id, payload)
          showSuccess('Perubahan Berhasil Disimpan!')
        } else {
          await createUserOverrideConfig(payload)
          showSuccess('Data Berhasil Ditambahkan!')
        }

        this.isDrawerVisible = false
        await this.fetchUserOverrideConfigs()
      } catch (error) {
        if (mode === 'edit') {
          showError(error, 'Gagal Memperbarui Data!')
        } else {
          showError(error, 'Gagal Menambahkan Data!')
        }
      } finally {
        this.isSubmitting = false
      }
    },
    handleDeleteOverride(item) {
      if (!item || !item.id) return
      const targetName = item.name || item.user_name || 'karyawan'
      this.modalConfirm.title = 'Hapus Konfigurasi Override Individu?'
      this.modalConfirm.description = confirmModalMessage(
        `Konfigurasi override approval lembur untuk <strong>${targetName}</strong> akan dihapus. Apakah Anda ingin melanjutkan?`,
      )
      this.modalConfirm.negativeButtonText = 'Batalkan'
      this.modalConfirm.negativeButtonAction = () => {
        this.$bvModal.hide(this.modalConfirm.id)
      }
      this.modalConfirm.positiveButtonText = 'Ya, Hapus'
      this.modalConfirm.positiveButtonAction = async () => {
        try {
          await deleteUserOverrideConfig(item.id)
          showSuccess('Perubahan Berhasil Disimpan!')
          this.isDrawerVisible = false
          this.$bvModal.hide(this.modalConfirm.id)
          await this.fetchUserOverrideConfigs()
        } catch (error) {
          showError(error, 'Gagal Memperbarui Data!')
        }
      }
      this.modalConfirm.closeButtonAction = () => {
        this.$bvModal.hide(this.modalConfirm.id)
      }

      this.$bvModal.show(this.modalConfirm.id)
    },
  },
}
</script>
